/** Client panel entry (TZ 08). Chooses real or mock API (VITE_USE_MOCKS), then mounts the router. */
import '@person-platform/ui/fonts.css';
import './styles/app.css';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createApiForEnvironment } from './app/api_bootstrap';
import { AppProviders } from './app/app_providers';
import { createAppQueryClient } from './app/query_client';
import { createAppRouter, type AppRouter } from './app/router';
import { createClientI18n } from './i18n/i18n_setup';

class MissingRootElementError extends Error {
  constructor() {
    super('Element #root is missing in index.html');
    this.name = 'MissingRootElementError';
  }
}

async function startClientPanel(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (rootElement === null) {
    throw new MissingRootElementError();
  }
  const api = await createApiForEnvironment();
  let appRouter: AppRouter | null = null;
  const queryClient = createAppQueryClient(() => {
    const currentPath = appRouter?.state.location.pathname ?? '/';
    if (appRouter !== null && currentPath !== '/login') {
      void appRouter.navigate({ to: '/login', search: { redirect: appRouter.state.location.href } });
    }
  });
  const i18n = createClientI18n();
  appRouter = createAppRouter({ queryClient, endpoints: api.endpoints });
  createRoot(rootElement).render(
    <StrictMode>
      <AppProviders api={api} queryClient={queryClient} i18n={i18n}>
        <RouterProvider router={appRouter} />
      </AppProviders>
    </StrictMode>,
  );
}

void startClientPanel();
